/**
 * One outstanding refund, rebuilt on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid and the side column's cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-details.tsx
 *   the record's label:value blocks;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   the bordered "latest attempt" callout under the tracker.
 * The header is `components/detail-parts.tsx`'s `PageHero`; the lifecycle is rizzui's `Stepper`, the
 * same tracker the order screen uses; `packages/theme/src/domain/{refund-queued,refund-failed,
 * caveat-banner}.tsx` still supply the state notices, unchanged.
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState, type ReactNode } from "react";
import toast from "react-hot-toast";
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiArrowClockwiseBold as RetryIcon,
  PiArrowCounterClockwiseDuotone as RefundIcon,
  PiChatCenteredTextDuotone as ResponseIcon,
  PiCreditCardDuotone as CardIcon,
  PiFingerprintDuotone as IdIcon,
  PiHandCoinsDuotone as HandCoinsIcon,
  PiHourglassMediumDuotone as InFlightIcon,
  PiLinkDuotone as LinkIcon,
  PiListBulletsDuotone as ListIcon,
  PiPlugsConnectedDuotone as ProviderIcon,
  PiReceiptDuotone as OrderIcon,
  PiTagDuotone as ReasonIcon,
  PiTruckDuotone as TrackerIcon,
} from "react-icons/pi";
import { Badge, Button, Stepper, Text } from "rizzui";
import { CaveatBanner, ErrorSurface, ReasonDialog, RefundFailed, RefundQueued, VerbatimErrorText, WidgetCard } from "@steelyard/theme";
import { can } from "../../lib/permissions";
import { canRetry, count, FAILED, moment, money, QUEUED, RETRY_SCHEDULE_NOTICE, refundByKey, retryRefund, type OutstandingRefund } from "../../lib/services/finance";
import { sentenceCase } from "../../lib/format";
import { ApiError } from "../../lib/api";
import { CardTitle, InfoBlock, PageHero, StatusPill } from "../../components/detail-parts";
import type { AccountSummary, PermissionState } from "../../types";
import { orderPath, refundPath } from "../../lib/paths";

/**
 * Ported from apps/admin/app/(app)/finance/refunds/[id]/page.tsx. Addressed by the refund's number
 * (`RFD-000012`, Billing's by-number read). A legacy id URL — a bookmark, or a stored notification
 * link — is looked up in the outstanding list and redirected; one not there is reported as such
 * ("not found" would claim more than was checked; a completed refund is absent from that list).
 *
 * T026: a retry exists for a `failed` refund with a recognised reason (`canRetry`,
 * lib/services/finance.ts) — `RefundPolicy.Committed` (backend) excludes `Failed` refunds from the
 * aggregate cap, so a fresh `requestId` for the same amount is a legitimate new attempt, not a
 * double-refund. `queued` stays without a retry control: it still reserves the cap, so the API
 * would 422 it anyway, and `RETRY_SCHEDULE_NOTICE` already tells a person to decide now.
 */
export async function refundDetailLoader({ params, request }: LoaderFunctionArgs) {
  // Billing sends each refund's order number, customer and provider reference beside the ids.
  return { refund: await refundByKey(request, params.refundNumber!) };
}

const BREADCRUMB = [{ name: "Home", href: "/" }, { name: "Finance" }, { name: "Refunds", href: "/finance/refunds" }];
const BACK = { href: "/finance/refunds", label: "Outstanding refunds" };

export default function RefundDetailPage() {
  const { refund } = useLoaderData() as { refund: OutstandingRefund | null };
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [retryError, setRetryError] = useState<string | undefined>();
  // Minted once per mount, not per submit-click, so a lost response and a resubmit of *this same
  // confirm* reuse one id — the part of FR-019 that a client-side mint has to hold up on its own.
  const [requestId] = useState(() => crypto.randomUUID());

  if (!can(permissionState, "billing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this refund" />;
  }
  const mayRetry = can(permissionState, "billing.administer");

  if (!refund) {
    return (
      <div className="flex flex-col gap-6">
        <PageHero breadcrumb={[...BREADCRUMB, { name: "Refund" }]} back={BACK} icon={<RefundIcon aria-hidden className="size-9" />} iconClassName="bg-blue" title="Refund" />
        <ErrorSurface
          error={{
            title: "This refund is not in the outstanding list",
            detail: "It may have already completed — completed refunds don't appear here.",
          }}
          action="show this refund"
        />
      </div>
    );
  }

  const retriable = mayRetry && canRetry(refund);
  const confirmingRetry = searchParams.get("confirm") === "retry";

  async function handleRetry(formData: FormData) {
    setRetryError(undefined);
    try {
      const result = await retryRefund(refund!.paymentId, {
        requestId,
        amount: refund!.amount,
        reason: refund!.reason,
        reasonNote: String(formData.get("reason") ?? "").trim() || null,
      });
      toast.success(`Retry ${result.status === "succeeded" ? "succeeded" : `now ${result.status}`}`);
      // Where the retry's response carries no number, its id URL redirects to it.
      navigate(refundPath(result.refundNumber || result.refundId), { replace: true });
    } catch (err) {
      setRetryError(err instanceof ApiError ? (err.detail ?? err.title ?? "The service refused this retry.") : "The service refused this retry.");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* ── Hero: what is owed, its state, and the one action that may be yours ─────────────── */}
      <PageHero
        breadcrumb={[...BREADCRUMB, { name: money(refund.amount) }]}
        back={BACK}
        icon={<RefundIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title={money(refund.amount)}
        description="Owed back to the customer and not yet with them."
        meta={
          <>
            <StatusPill status={refund.status} />
            <Badge variant="flat" color="secondary" rounded="pill" className="gap-1.5">
              <ReasonIcon aria-hidden className="size-4" />
              {sentenceCase(refund.reason)}
            </Badge>
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <ProviderIcon aria-hidden className="size-4" />
              {refund.provider}
            </Badge>
          </>
        }
        actions={
          <>
            <Link to={orderPath(refund.orderNumber || refund.orderId)}>
              <Button as="span" variant="outline">
                <OrderIcon aria-hidden className="me-1.5 size-4" />
                Open order
              </Button>
            </Link>
            {retriable ? (
              <Link to={`${refundPath(refund.refundNumber || refund.refundId)}?confirm=retry`}>
                <Button as="span">
                  <RetryIcon aria-hidden className="me-1.5 size-4" />
                  Retry this refund
                </Button>
              </Link>
            ) : null}
          </>
        }
      />

      {refund.status === QUEUED ? (
        <RefundQueued amount={money(refund.amount)} attempts={count(refund.attempts)} nextAttempt="not published by the service">
          <p className="text-muted-foreground text-sm">{RETRY_SCHEDULE_NOTICE}</p>
        </RefundQueued>
      ) : refund.status === FAILED ? (
        <RefundFailed amount={money(refund.amount)} providerError={refund.providerError ?? "The service retained no text for this refusal."} order={<OrderLink orderId={refund.orderId} orderNumber={refund.orderNumber} />} />
      ) : (
        <UnrecognisedState refund={refund} />
      )}

      {retriable ? (
        <ReasonDialog
          title="Retry this refund"
          open={confirmingRetry}
          consequence={
            <>
              A new refund request for {money(refund.amount)} is sent with a fresh request id. It cannot double-refund the customer: this refund never moved money, so it is not counted against the amount still refundable on this
              payment.
            </>
          }
          action={handleRetry}
          confirmLabel="Retry the refund"
          error={retryError}
          cancel={
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setSearchParams((p) => {
                  p.delete("confirm");
                  return p;
                })
              }
            >
              Not now
            </Button>
          }
        />
      ) : null}

      <div className="grid items-start gap-6 @container lg:grid-cols-12">
        <div className="flex flex-col gap-6 lg:col-span-8">
          <RefundProgress refund={refund} />

          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<ResponseIcon aria-hidden className="size-5" />} tile="bg-red-lighter text-red-dark">
                The provider&rsquo;s response
              </CardTitle>
            }
            description={<Text className="ms-[52px] text-sm text-gray-500">Retained verbatim.</Text>}
          >
            <div className="pt-4">
              {refund.providerError ? (
                <VerbatimErrorText label="The provider's response, verbatim">{refund.providerError}</VerbatimErrorText>
              ) : (
                <Text className="text-sm text-gray-500">
                  The service retained no error text for this refund. That is not an empty refusal — on a queued refund it is the ordinary case, because the provider was never reached and so never said anything.
                </Text>
              )}
            </div>
          </WidgetCard>

          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<HandCoinsIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
                What you can do
              </CardTitle>
            }
          >
            <div className="pt-4">
              {retriable ? (
                <Text className="text-sm text-gray-700">
                  This refund failed for a reason the service recognises, so a fresh attempt for the same amount is safe — it never moved money and does not count against what is still refundable. Use <strong>Retry this refund</strong> above;
                  you will be asked why.
                </Text>
              ) : (
                <RetryNotAvailable mayRetry={mayRetry} refund={refund} />
              )}
            </div>
          </WidgetCard>
        </div>

        <div className="flex flex-col gap-6 lg:col-span-4">
          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<IdIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
                Record
              </CardTitle>
            }
          >
            <div className="mt-5 flex flex-col gap-4">
              <InfoBlock icon={<RefundIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Customer">
                <Text className="text-sm font-medium break-all text-gray-900">{refund.customerName ?? "Unknown customer"}</Text>
              </InfoBlock>
              <InfoBlock icon={<ReasonIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Reason">
                <Text className="text-sm font-medium text-gray-900">{sentenceCase(refund.reason)}</Text>
                <Text className="font-mono text-xs text-gray-500">{refund.reason}</Text>
              </InfoBlock>
              <InfoBlock icon={<ProviderIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Provider">
                <Text className="font-mono text-sm break-all text-gray-900">{refund.provider}</Text>
              </InfoBlock>
              <InfoBlock icon={<ListIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Status, as the service spells it">
                <Text className="font-mono text-sm break-all text-gray-900">{refund.status}</Text>
              </InfoBlock>
            </div>
          </WidgetCard>

          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<LinkIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
                Linked records
              </CardTitle>
            }
          >
            <div className="mt-5 flex flex-col gap-4">
              <InfoBlock icon={<OrderIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="The order this is about">
                <OrderLink orderId={refund.orderId} orderNumber={refund.orderNumber} />
                <Text className="text-xs text-gray-500">It stays open until the refund is confirmed or its failure recorded.</Text>
              </InfoBlock>
              <InfoBlock icon={<CardIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="The payment behind it">
                <LinkOut to={`/finance/transactions?paymentId=${encodeURIComponent(refund.paymentId)}`}>Transaction {refund.providerReference ?? "—"}</LinkOut>
                <Text className="text-xs text-gray-500">Its provider reference and how it was resolved.</Text>
              </InfoBlock>
              <InfoBlock icon={<ListIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="On the same payment">
                <LinkOut to={`/finance/refunds?paymentId=${encodeURIComponent(refund.paymentId)}`}>Every refund for it</LinkOut>
              </InfoBlock>
            </div>
          </WidgetCard>
        </div>
      </div>
    </div>
  );
}

/* ── Lifecycle ────────────────────────────────────────────────────────────────────────────── */

type StepStatus = "waiting" | "in-progress" | "completed" | "error";
const STEP_COLOR: Record<StepStatus, "success" | "info" | "danger"> = { completed: "success", "in-progress": "info", error: "danger", waiting: "info" };

/**
 * Where a refund sits between being raised and reaching the customer. Only the two states this list
 * can carry are placed: queued never reached the provider; failed reached it and was refused. Any
 * other status is not placed at all — `UnrecognisedState` already says why.
 */
function steps(status: string): { title: string; status: StepStatus; description?: string }[] | null {
  if (status === QUEUED) {
    return [
      { title: "Raised", status: "completed" },
      { title: "Reached the provider", status: "in-progress", description: "Not reached yet" },
      { title: "Provider decided", status: "waiting" },
      { title: "With the customer", status: "waiting" },
    ];
  }
  if (status === FAILED) {
    return [
      { title: "Raised", status: "completed" },
      { title: "Reached the provider", status: "completed", description: "Answered" },
      { title: "Provider decided", status: "error", description: "Refused" },
      { title: "With the customer", status: "waiting" },
    ];
  }
  return null;
}

function RefundProgress({ refund }: { refund: OutstandingRefund }) {
  const placed = steps(refund.status);
  if (!placed) return null;
  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<TrackerIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
          Refund progress
        </CardTitle>
      }
    >
      <div className="-mx-1 mt-6 overflow-x-auto px-1 pb-2">
        <Stepper className="min-w-[560px]" titleClassName="text-sm font-semibold" descriptionClassName="text-xs">
          {placed.map((step) => (
            <Stepper.Step
              key={step.title}
              title={step.title}
              description={step.description}
              status={step.status}
              color={STEP_COLOR[step.status]}
              size="sm"
              icon={step.status === "in-progress" ? <InFlightIcon aria-hidden className="size-4" /> : undefined}
            />
          ))}
        </Stepper>
      </div>
      <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 rounded-lg border border-s-4 border-muted border-s-secondary bg-secondary-lighter/30 px-4 py-3 dark:bg-secondary-lighter/10">
        <div>
          <Text className="text-xs font-semibold uppercase tracking-wider text-secondary-dark">Last attempt</Text>
          <Text className="mt-0.5 text-sm text-gray-900">{moment(refund.lastAttemptAt)}</Text>
        </div>
        <div>
          <Text className="text-xs font-semibold uppercase tracking-wider text-secondary-dark">Attempts so far</Text>
          <Text className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900">{count(refund.attempts)}</Text>
        </div>
      </div>
    </WidgetCard>
  );
}

/* ── Pieces ───────────────────────────────────────────────────────────────────────────────── */

function RetryNotAvailable({ mayRetry, refund }: { mayRetry: boolean; refund: OutstandingRefund }) {
  const caveat = !mayRetry
    ? "You do not have permission to retry a refund"
    : refund.status === QUEUED
      ? "There is no retry button here — this refund is still queued"
      : refund.status === FAILED
        ? "There is no retry button here — this refund's reason is not one this screen recognises"
        : "There is no retry button here — this status is not one this screen recognises";

  const explanation = !mayRetry
    ? "Retrying a refund moves money and is gated separately from viewing this record."
    : refund.status === QUEUED
      ? "A queued refund still reserves the amount against this payment's cap, so a fresh attempt for the same amount would be refused. Use the payment service's own backoff retry instead — run by an operator or a scheduler, not from a screen."
      : refund.status === FAILED
        ? `The reason recorded is "${refund.reason}", which this screen does not recognise well enough to resubmit blind. Retrying risks a 422 for an unknown reason, or worse, silently sending the wrong one.`
        : "Retrying from here risks paying the customer twice. Use the payment service's own replay process instead — run by an operator or a scheduler, not from a screen.";

  return (
    <CaveatBanner caveat={caveat}>
      <p className="text-muted-foreground text-sm">{explanation}</p>
    </CaveatBanner>
  );
}

/**
 * An unrecognised status is a genuine data-integrity anomaly ("a third outstanding state is a
 * contract change") and must still interrupt a screen reader. `CaveatBanner` itself is
 * `role="note"` by contract, so the outer `<div role="alert">` adds the semantic role without
 * touching CaveatBanner's own markup.
 */
function UnrecognisedState({ refund }: { refund: OutstandingRefund }) {
  return (
    <div role="alert">
      <CaveatBanner
        caveat={
          <>
            This refund&rsquo;s status is <span className="font-mono">{refund.status}</span>, which this screen does not recognise.
          </>
        }
      >
        <p className="text-muted-foreground text-sm">
          Showing it as queued or failed could point you to the wrong action, so it isn&rsquo;t shown as either. {money(refund.amount)} is recorded against this refund — please report this status.
        </p>
      </CaveatBanner>
    </div>
  );
}

function LinkOut({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-sm font-medium break-all text-blue-dark hover:underline">
      {children}
    </Link>
  );
}

function OrderLink({ orderId, orderNumber }: { orderId: string; orderNumber?: string | null }) {
  return (
    <Link to={orderPath(orderNumber || orderId)} className="text-sm font-medium break-all text-blue-dark hover:underline">
      Order {orderNumber ?? "—"} — still open
    </Link>
  );
}
